import { Component } from '@theme/component';

/**
 * @typedef {Object} LeadQualificationFormRefs
 * @property {HTMLElement} formWrapper
 * @property {HTMLElement} errorSummary
 * @property {HTMLElement} errorHeading
 * @property {HTMLElement} successMessage
 * @property {HTMLElement} successHeading
 * @property {HTMLElement} consentText
 * @property {HTMLButtonElement} submitButton
 * @property {HTMLInputElement} name
 * @property {HTMLInputElement} email
 * @property {HTMLInputElement} phone
 * @property {HTMLInputElement} phoneOptIn
 * @property {HTMLInputElement} shopifyStoreUrl
 * @property {HTMLSelectElement} helpNeeded
 * @property {HTMLSelectElement} budgetRange
 * @property {HTMLSelectElement} timeline
 */

/**
 * Lead qualification form built on Shopify's native contact form.
 * Validates client-side, then submits via fetch so the page doesn't reload.
 * @extends {Component<LeadQualificationFormRefs>}
 */
class LeadQualificationForm extends Component {
  requiredRefs = ['formWrapper', 'successMessage'];

  /** @type {HTMLFormElement | null} */
  #form = null;

  /** @type {string} */
  #defaultSubmitLabel = '';

  /** @type {boolean} Guards against firing the conversion event more than once. */
  #captured = false;

  connectedCallback() {
    super.connectedCallback();

    this.#form = this.querySelector('form');
    this.#defaultSubmitLabel = this.refs.submitButton?.textContent?.trim() ?? '';

    // No submit button means the server-rendered success state is showing; nothing to wire up.
    if (this.#form && this.refs.submitButton) {
      this.#form.addEventListener('submit', (event) => this.handleSubmit(event));
    }

    this.#syncConsentCopy();
    this.refs.phone?.addEventListener('input', () => this.#syncConsentCopy());
    this.refs.phoneOptIn?.addEventListener('change', () => this.#syncConsentCopy());

    // Covers the no-JS / full-page-reload path (Shopify redirects to ?contact_posted=true).
    this.#handlePostedRedirect();
  }

  /**
   * @param {SubmitEvent} event
   */
  async handleSubmit(event) {
    event.preventDefault();

    if (!this.#form) return;

    this.#clearErrors();

    const validation = this.#validate();
    if (!validation.valid) {
      this.#showFieldErrors(validation.errors);
      return;
    }

    const formData = new FormData(this.#form);

    // Phone opt-in gates whether the phone number is included in the submission.
    const optedIn = Boolean(this.refs.phoneOptIn?.checked);
    if (!optedIn) {
      formData.delete('contact[phone]');
    }

    const submitButton = this.refs.submitButton;
    const previousLabel = submitButton.textContent;

    submitButton.disabled = true;
    submitButton.setAttribute('aria-busy', 'true');
    if (this.dataset.submittingLabel) {
      submitButton.textContent = this.dataset.submittingLabel;
    }

    try {
      const response = await fetch(this.#form.action, {
        method: 'POST',
        body: formData,
        headers: { Accept: 'text/html' },
      });

      // Shopify redirects to ?contact_posted=true only on a genuinely successful post
      // (fetch follows the redirect, so it lands in response.url). A 200 without that
      // param means the server rejected the submission — response.ok is true either way,
      // so it can't be trusted on its own.
      const succeeded = response.ok && response.url.includes('contact_posted=true');

      if (!succeeded) {
        throw new Error(`Contact form submission was not accepted (status ${response.status})`);
      }

      this.#showSuccess();
    } catch (error) {
      console.error('Lead qualification form submission failed:', error);
      this.#showFormError(this.dataset.errorGeneric || 'Something went wrong. Please try again.');
      submitButton.disabled = false;
      submitButton.removeAttribute('aria-busy');
      submitButton.textContent = previousLabel || this.#defaultSubmitLabel;
    }
  }

  /**
   * @returns {{ valid: boolean, errors: Array<{ message: string, element: HTMLElement }> }}
   */
  #validate() {
    /** @type {Array<{ message: string, element: HTMLElement }>} */
    const errors = [];
    const { name, email, helpNeeded, timeline } = this.refs;

    if (!name?.value.trim()) {
      errors.push({ message: this.dataset.errorNameRequired || 'Name is required.', element: name });
    }

    const emailValue = email?.value.trim() ?? '';
    if (!emailValue) {
      errors.push({ message: this.dataset.errorEmailRequired || 'Email is required.', element: email });
    } else if (!this.#isValidEmail(emailValue)) {
      errors.push({ message: this.dataset.errorEmailInvalid || 'Enter a valid email address.', element: email });
    }

    if (!helpNeeded?.value) {
      errors.push({
        message: this.dataset.errorHelpRequired || 'Please select what you need help with.',
        element: helpNeeded,
      });
    }

    if (!timeline?.value) {
      errors.push({
        message: this.dataset.errorTimelineRequired || 'Please select a timeline.',
        element: timeline,
      });
    }

    return { valid: errors.length === 0, errors };
  }

  /**
   * @param {string} email
   */
  #isValidEmail(email) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  }

  /**
   * @param {Array<{ message: string, element: HTMLElement }>} errors
   */
  #showFieldErrors(errors) {
    const { errorSummary, errorHeading } = this.refs;
    const list = errorSummary.querySelector('[ref="errorList"]') || errorSummary.querySelector('ul');

    if (list) {
      list.replaceChildren();

      for (const error of errors) {
        const fieldId = error.element.id;
        const messageId = `${fieldId}-error`;
        let messageEl = this.querySelector(`#${CSS.escape(messageId)}`);

        if (!(messageEl instanceof HTMLElement)) {
          messageEl = document.createElement('p');
          messageEl.id = messageId;
          messageEl.className = 'lead-form__field-error';
          messageEl.setAttribute('role', 'alert');
          error.element.insertAdjacentElement('afterend', messageEl);
        }

        messageEl.hidden = false;
        messageEl.textContent = error.message;
        error.element.setAttribute('aria-invalid', 'true');
        error.element.setAttribute('aria-describedby', messageId);

        const item = document.createElement('li');
        const link = document.createElement('a');
        link.href = `#${fieldId}`;
        link.textContent = error.message;
        link.addEventListener('click', (clickEvent) => {
          clickEvent.preventDefault();
          error.element.focus();
        });
        item.appendChild(link);
        list.appendChild(item);
      }
    }

    errorSummary.hidden = false;
    this.#focusElement(errorHeading, errorSummary);
  }

  /**
   * @param {string} message
   */
  #showFormError(message) {
    const { errorSummary, errorHeading } = this.refs;
    const list = errorSummary.querySelector('[ref="errorList"]') || errorSummary.querySelector('ul');

    if (list) {
      list.replaceChildren();
      const item = document.createElement('li');
      item.textContent = message;
      list.appendChild(item);
    }

    errorSummary.hidden = false;
    this.#focusElement(errorHeading, errorSummary);
  }

  #showSuccess() {
    const { formWrapper, successMessage, successHeading, errorSummary } = this.refs;

    formWrapper.hidden = true;
    errorSummary.hidden = true;
    successMessage.hidden = false;

    this.#focusElement(successHeading, successMessage);

    this.#capturePosthog({
      help_needed: this.refs.helpNeeded?.value,
      timeline: this.refs.timeline?.value,
      has_phone: Boolean(this.refs.phoneOptIn?.checked && this.refs.phone?.value.trim()),
    });
  }

  /**
   * Handles the full-page-reload path used when JS doesn't intercept the submit
   * (e.g. progressive enhancement fallback). Shopify redirects back with
   * ?contact_posted=true and the server renders the success markup inside the form.
   * We fire the conversion event, move focus to the message, and strip the query
   * param so a refresh doesn't re-trigger the event.
   */
  #handlePostedRedirect() {
    const params = new URLSearchParams(window.location.search);
    if (params.get('contact_posted') !== 'true') return;

    // When the server renders the success state it replaces the fields (and submit
    // button), so an absent submit button means this form is the one that posted.
    if (this.refs.submitButton) return;

    this.#capturePosthog();

    const successBlock = this.#form?.querySelector('.lead-form__success');
    const successHeading = successBlock?.querySelector('.lead-form__success-heading');
    this.#focusElement(successHeading ?? undefined, successBlock ?? undefined);

    params.delete('contact_posted');
    const query = params.toString();
    const cleanUrl = window.location.pathname + (query ? `?${query}` : '') + window.location.hash;
    window.history.replaceState({}, '', cleanUrl);
  }

  /**
   * Fires the PostHog conversion event once per page load.
   * @param {Record<string, unknown>} [properties]
   */
  #capturePosthog(properties = {}) {
    if (this.#captured) return;
    this.#captured = true;

    if (typeof window.posthog?.capture === 'function') {
      window.posthog.capture('Lead Form Submitted', {
        page_path: window.location.pathname,
        ...properties,
      });
    }
  }

  #clearErrors() {
    const { errorSummary } = this.refs;
    const list = errorSummary.querySelector('[ref="errorList"]') || errorSummary.querySelector('ul');

    if (list) {
      list.replaceChildren();
    }

    errorSummary.hidden = true;

    for (const field of this.querySelectorAll('[aria-invalid="true"]')) {
      field.removeAttribute('aria-invalid');
      field.removeAttribute('aria-describedby');
    }

    for (const message of this.querySelectorAll('.lead-form__field-error')) {
      if (message instanceof HTMLElement) {
        message.hidden = true;
        message.textContent = '';
      }
    }
  }

  /**
   * Moves focus (and scrolls) to a heading, falling back to its container.
   * @param {HTMLElement} [heading]
   * @param {HTMLElement} [fallback]
   */
  #focusElement(heading, fallback) {
    requestAnimationFrame(() => {
      const target = heading instanceof HTMLElement ? heading : fallback;
      if (target instanceof HTMLElement) {
        target.focus();
        target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    });
  }

  #syncConsentCopy() {
    const { consentText, phone, phoneOptIn } = this.refs;
    if (!(consentText instanceof HTMLElement)) return;

    const base = this.dataset.consentBase || '';
    const phoneSuffix = this.dataset.consentPhoneSuffix || '';
    const includePhone = Boolean(phone?.value.trim()) && Boolean(phoneOptIn?.checked);

    consentText.textContent = includePhone && phoneSuffix ? `${base} ${phoneSuffix}` : base;
  }
}

if (!customElements.get('lead-qualification-form')) {
  customElements.define('lead-qualification-form', LeadQualificationForm);
}
